import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import Spinner from './Spinner'
import { FilterAdjustments, filterUtils } from '@renderer/utils/filterUtils'
import {
  getCachedLUT,
  getCachedOverlayImage,
  fetchOverlayImage,
  applyFilterOverlayImage,
  type FilterOverlayImage
} from '@renderer/utils/lutUtils'
import { computeBitmapCropFromSensor, computeExplicitCropFromSensor } from '@renderer/utils/previewLayout'
import {
  publishSharedPreviewCapture,
  peekSharedPreviewCapture,
  type PreviewCaptureShareGroupId
} from '@renderer/utils/previewCaptureShare'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import Stats from 'stats.js'
import { isDigitalMode } from '@renderer/lib/runtime'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import FilterWorker from '@renderer/workers/filter.worker.ts?worker'
import { imageUtils } from '@renderer/utils/imageUtils'
import {
  saveOriginalSettings,
  applyLiveViewBrightness,
  restoreOriginalSettings,
  type OriginalSettings
} from '@renderer/utils/cameras/cameraSettingsManager'
import { useCameraStore } from '@renderer/stores/cameraStore'
import {
  isSamePreviewDevice,
  readPreviewDeviceStamp,
  type PreviewDeviceStamp
} from '@renderer/utils/cameras/previewDeviceBinding'
import { useCameraRestStore } from '@renderer/stores/cameraRestStore'
import { acquireCameraPreview } from '@renderer/utils/cameras/previewRefcount'
import { cameraLog } from '@renderer/utils/cameras/cameraLog'
import { describeError } from '@renderer/utils/telemetry/boothLogClient'

/**
 * Default liveview pull rate for a TETHERED DSLR when the operator has not set
 * previewQuality.maxFps. Without a default the rAF loop pulls PTP liveview frames as
 * fast as the camera returns them (30-60/s) for every minute a Preview is mounted —
 * needless USB contention, sensor heat, and shutter-mechanism wear on booths that idle
 * on a preview page all day. 15 fps is indistinguishable on a booth screen. Webcams are
 * exempt (their frames are local canvas grabs, effectively free). An explicit maxFps
 * setting always wins.
 */
const DEFAULT_DSLR_PREVIEW_MAX_FPS = 15

interface PreviewOptions {
  maxWidth?: number
  maxHeight?: number
  quality?: number
}

type CameraKey = 'primary' | 'secondary'

interface PreviewProps {
  capturePreview?: (cameraKey: CameraKey, options?: PreviewOptions) => Promise<Blob | undefined>
  adjustments?: FilterAdjustments
  filter?: string // Used as a key for re-renders
  postLUTAdjustments?: {
    vignette?: number
    grain?: number
    blur?: number
    clarity?: number
    sharpeningAmount?: number
    bloom?: number
    softFocus?: number
    halation?: number
  }
  /** The selected filter's overlay layer, applied in both Manual and LUT modes. */
  filterOverlay?: FilterOverlayImage | null
  ratio?: number
  cameraRatio?: number
  previewQuality?: PreviewOptions
  onCanvasReady?: (dimensions: { width: number; height: number }) => void
  /** 2D canvas with previewRotation + mirror baked into pixels for WebCodecs recorders */
  onRecordingCanvasReady?: (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    dimensions: { width: number; height: number }
  ) => void
  cameraKey?: CameraKey
  isPaused?: boolean
  fillContainer?: boolean // If true, fill container (object-cover behavior) instead of centering
  /**
   * Explicit crop rectangle, normalized [0,1] in UNROTATED sensor space. When provided it
   * OVERRIDES the centered ratio-based crop (Bahelakotak custom off-center box). Combine with
   * `fillContainer` + a matching `ratio` (the crop's final display aspect) so the cropped
   * region fills the pane. Ignored by every other booth, which never passes it.
   */
  cropRect?: { x: number; y: number; w: number; h: number }
  /**
   * Force the raw, uncropped sensor feed by ignoring the booth's captureRatio (L2) setting.
   * The Bahelakotak admin editor needs to show the WHOLE lens so the operator can frame the
   * custom box; without this a non-'auto' captureRatio would pre-crop the alignment view.
   */
  forceFullSensor?: boolean
  /** Producer captures from camera; consumer reuses the producer's blob for this group (same cameraKey). */
  captureShareRole?: 'producer' | 'consumer'
  captureShareGroupId?: PreviewCaptureShareGroupId
  /**
   * Override the camera settings this preview renders with, instead of reading the SAVED store
   * settings. Lets the Camera Settings modal show a live preview that reacts to the operator's
   * UNSAVED edits (mirror, rotation, capture ratio, preview quality) before they hit Save. When
   * omitted (every normal caller) the preview keeps reading the store as before.
   */
  settingsOverride?: {
    captureRatio?: number | 'auto'
    mirror?: boolean
    previewRotation?: number
  }
}

const DEFAULT_PREVIEW_QUALITY = {
  maxWidth: 1280,
  maxHeight: 720,
  quality: 0.8,
  resizeQuality: 'medium' as const
}

const Preview: React.FC<PreviewProps> = ({
  capturePreview = unifiedCameraUtils.capturePreview,
  ratio,
  cameraRatio,
  adjustments,
  filter,
  postLUTAdjustments,
  filterOverlay,
  onCanvasReady,
  onRecordingCanvasReady,
  cameraKey = useSessionStateStore().userPreferences.selectedCamera as 'primary' | 'secondary',
  isPaused: isPausedProp = false,
  fillContainer = false,
  cropRect,
  forceFullSensor = false,
  captureShareRole,
  captureShareGroupId,
  settingsOverride
}) => {
  // Camera Rest: while this cameraKey is parked (connection-preserving sleep), suspend
  // the pull loop exactly like an explicit pause — the canvas keeps its last frame and
  // the loop resumes automatically on wake (the store flag flips, the effect re-runs).
  const isParked = useCameraRestStore((s) => s.parked[cameraKey])
  const isPaused = isPausedProp || isParked

  // A mounted, running Preview IS the "camera needed" signal — acquire on mount
  // (wakes a parked camera), release on unmount. Acquiring is UNCONDITIONAL by
  // flag: the count is what every park authority checks before parking, so a
  // liveview that is pulling frames can never be parked out from under the guest
  // (the Welcome-idle timer used to do exactly that on single-page booths). Only
  // the park-on-release linger inside previewRefcount stays behind the
  // `camera_preview_refcount` flag.
  //
  // An explicitly paused preview (isPausedProp) holds NO reference — it reads no
  // frames, so it must not block resting. `isParked` is deliberately not part of
  // this: the acquire is what un-parks, so keying off it would deadlock.
  useEffect(() => {
    if (isPausedProp) return undefined
    return acquireCameraPreview(cameraKey)
  }, [cameraKey, isPausedProp])

  const { cameraSettings, secondaryCameraSettings, debugFps, selectedCamera } = useSettingsStore()
  const storeSettings = cameraKey === 'primary' ? cameraSettings : secondaryCameraSettings
  // A settings override (the inline camera editor's live preview) wins over the saved
  // store settings. Memoised BY CONTENT: the big effect below lists `settings` in its
  // deps and (re)starts the frame loop, and without an override `settings` is the store
  // object — stable across renders. A fresh merged object every render would restart
  // the loop on every parent re-render (SettingsModal re-renders once a second while
  // the pro-trial countdown runs), which showed up as a stuttering, sped-up preview.
  const settingsOverrideKey = settingsOverride ? JSON.stringify(settingsOverride) : ''
  const settings = useMemo(
    () => (settingsOverride ? { ...storeSettings, ...settingsOverride } : storeSettings),
    [storeSettings, settingsOverrideKey]
  )

  // Add minimal validation for settings (do not default captureRatio to 4:3 — that re-enables
  // L2 crop when the setting is missing; same semantics as getNormalizedCaptureRatio().)
  const rawSettingsCaptureRatio = settings?.captureRatio
  const mirror = settings?.mirror ?? true
  const applyFilterOnPreview = settings?.applyFilterOnPreview ?? true
  // Same identity every render: `previewQuality` is a dep of the frame-loop effect, and
  // a fresh fallback literal would restart the loop on every render of a booth whose
  // settings never set it.
  const previewQuality = settings?.previewQuality ?? DEFAULT_PREVIEW_QUALITY
  const rotation = settings?.previewRotation ?? 0

  const normalizedCaptureRatio =
    rawSettingsCaptureRatio === 'auto'
      ? undefined
      : typeof rawSettingsCaptureRatio === 'number' &&
          Number.isFinite(rawSettingsCaptureRatio) &&
          rawSettingsCaptureRatio > 0
        ? rawSettingsCaptureRatio
        : undefined

  const effectiveCameraRatio = forceFullSensor ? undefined : cameraRatio ?? normalizedCaptureRatio

  const [isCanvasLoaded, setIsCanvasLoaded] = useState(false)
  const isWorkerReadyRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  /** Latest onCanvasReady for video encoder setup — ref avoids stale closures in worker/rAF paths. */
  const onCanvasReadyRef = useRef<PreviewProps['onCanvasReady']>(onCanvasReady)
  onCanvasReadyRef.current = onCanvasReady
  const onRecordingCanvasReadyRef = useRef<PreviewProps['onRecordingCanvasReady']>(onRecordingCanvasReady)
  onRecordingCanvasReadyRef.current = onRecordingCanvasReady
  /** Draw filtered/cropped bitmap → recording buffer (avoids readback from display canvas). */
  const syncRecordingFromBitmapRef = useRef<((bmp: ImageBitmap) => void) | null>(null)
  const lastPreviewFrameTimeRef = useRef(0)
  const lastLutWorkerFilterRef = useRef<string>('')
  /** Signature of the overlay last pushed to the worker, so it is not re-sent per frame. */
  const lastOverlayWorkerKeyRef = useRef<string>('')
  const recordingCanvasRef = useRef<HTMLCanvasElement>(null)
  const lastReportedRecordingDimsRef = useRef<{ width: number; height: number } | null>(null)
  /** Skip redundant parent updates when backing-store size is unchanged. */
  const lastReportedCanvasDimsRef = useRef<{ width: number; height: number } | null>(null)
  /**
   * Sensor aspect ratio (originalWidth / originalHeight) discovered in the last getBitmapParam()
   * call. Used for layout sizing when effectiveCameraRatio is undefined ('auto') so the preview
   * box matches the real camera feed instead of falling back to a hardcoded 4:3.
   */
  const sensorAspectRef = useRef<number | undefined>(undefined)
  /** Set inside preview effect so getBitmapParam can re-run layout when sensor aspect is first known (auto ratio). */
  const updateCanvasSizeRef = useRef<(() => void) | null>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<number>()
  const throttledRef = useRef<number>(0)
  const isUnmountedRef = useRef(false)
  /**
   * Generation of the live frame loop. `isUnmountedRef` is shared by every run of the frame
   * effect, and the NEXT run sets it back to false — which revived the previous run's loop
   * while it sat awaiting a frame. Two loops then drove the same canvas, each with its own
   * crop rectangle, so after a camera switch the preview flickered between two zoom levels.
   * Each run takes its own id and stops the moment that id is no longer the current one.
   */
  const previewRunIdRef = useRef(0)
  const isWorkerBusyRef = useRef(false)
  const [error, setError] = useState<string>()
  /** The last frame error behind a "Failed to load camera preview", for the camera log. */
  const lastFrameErrorRef = useRef<Record<string, unknown> | null>(null)
  // The text on screen is all the operator reports; log it with what caused it.
  useEffect(() => {
    if (!error) return
    cameraLog.uiErrorShown('Preview', error, {
      cameraKey,
      lastFrameError: lastFrameErrorRef.current
    })
  }, [error, cameraKey])
  // FPS debug overlay is an operator diagnostic — never show it in digital mode.
  const statsRef = useRef<Stats | null>(debugFps && !isDigitalMode() ? new Stats() : null)
  const filterWorkerRef = useRef<Worker>()
  const currentCameraKeyRef = useRef<CameraKey>(cameraKey)
  const originalSettingsRef = useRef<OriginalSettings | null>(null)
  const isLiveViewBrightnessAppliedRef = useRef<boolean>(false)

  // Setup worker
  useEffect(() => {
    filterWorkerRef.current = new FilterWorker()
    lastLutWorkerFilterRef.current = ''
    const canvas = canvasRef.current
    const canvasCtx = canvas?.getContext('bitmaprenderer')

    const worker = filterWorkerRef.current
    worker.onmessage = (e: MessageEvent): void => {
      isWorkerReadyRef.current = true

      if (e.data.filteredBitmap) {
        const filteredBitmap = e.data.filteredBitmap as ImageBitmap
        if (canvasCtx) {
          syncRecordingFromBitmapRef.current?.(filteredBitmap)
          canvasCtx.transferFromImageBitmap(filteredBitmap)
          const cb = onCanvasReadyRef.current
          const el = canvasRef.current
          if (cb && el && el.width > 0 && el.height > 0) {
            const w = el.width
            const h = el.height
            const last = lastReportedCanvasDimsRef.current
            if (!last || last.width !== w || last.height !== h) {
              lastReportedCanvasDimsRef.current = { width: w, height: h }
              cb({ width: w, height: h })
            }
          }
        } else {
          filteredBitmap.close()
        }
      }
      isWorkerBusyRef.current = false
    }

    return (): void => {
      worker.terminate()
    }
  }, [])

  // This effect will reset isWorkerReady whenever the filter adjustments change.
  // This ensures the new filter is applied instantly on the main thread
  // before the worker takes over again.
  useEffect(() => {
    isWorkerReadyRef.current = false
    lastLutWorkerFilterRef.current = ''
  }, [filter])

  /**
   * Warm the overlay decode when the filter's overlay changes.
   *
   * Fire-and-forget by design: the draw paths read the cache synchronously and skip the
   * overlay on a miss, so the worst case of a slow decode is a few frames of the grade
   * without its texture — never a stall in front of a customer.
   */
  useEffect(() => {
    if (!filterOverlay?.url) return
    void fetchOverlayImage(filterOverlay.url)
  }, [filterOverlay?.url])

  /**
   * Push the current overlay to the worker, at most once per change.
   *
   * The bitmap is *cloned* into the message rather than transferred: transferring would
   * detach it from the main thread, and the main-thread fallback path below needs the
   * same image. The clone costs one copy per filter change, not per frame.
   */
  const syncOverlayToWorker = useCallback((): void => {
    const worker = filterWorkerRef.current
    if (!worker) return

    const image = filterOverlay?.url ? getCachedOverlayImage(filterOverlay.url) : null
    const key = image
      ? `${filterOverlay?.url}|${filterOverlay?.blendMode ?? ''}|${
          filterOverlay?.opacity ?? ''
        }|${filterOverlay?.fit ?? ''}`
      : ''
    if (key === lastOverlayWorkerKeyRef.current) return
    lastOverlayWorkerKeyRef.current = key

    worker.postMessage({
      type: 'overlayInit',
      bitmap: image ?? null,
      style: filterOverlay
        ? {
            blendMode: filterOverlay.blendMode,
            opacity: filterOverlay.opacity,
            fit: filterOverlay.fit
          }
        : undefined
    })
  }, [filterOverlay])

  const getBitmapParam = useCallback(async (): Promise<
    | {
        sx: number
        sy: number
        cropWidth: number
        cropHeight: number
        originalWidth: number
        originalHeight: number
      }
    | undefined
  > => {
    try {
      let originalWidth: number
      let originalHeight: number

      if (captureShareRole === 'consumer' && captureShareGroupId) {
        const shared = peekSharedPreviewCapture(captureShareGroupId)
        if (!shared || shared.cameraKey !== cameraKey || isUnmountedRef.current) return undefined
        originalWidth = shared.originalWidth
        originalHeight = shared.originalHeight
      } else {
        const blob = await capturePreview(cameraKey, {
          ...previewQuality,
          rotation: 0,
          mirror: false
        })
        if (!blob || isUnmountedRef.current) return undefined

        const img = await createImageBitmap(blob)
        originalWidth = img.width
        originalHeight = img.height

        if (originalWidth <= 0 || originalHeight <= 0) {
          img.close()
          console.warn('Invalid image dimensions:', originalWidth, originalHeight)
          return undefined
        }
        img.close()

        if (captureShareRole === 'producer' && captureShareGroupId) {
          publishSharedPreviewCapture(captureShareGroupId, cameraKey, blob, originalWidth, originalHeight)
        }
      }

      const newSensor = originalWidth / originalHeight
      const prevSensor = sensorAspectRef.current
      sensorAspectRef.current = newSensor
      if (
        effectiveCameraRatio === undefined &&
        (prevSensor === undefined || Math.abs(prevSensor - newSensor) > 0.005)
      ) {
        updateCanvasSizeRef.current?.()
      }

      // An explicit crop rect (kotak custom box) wins over the centered ratio-based crop.
      const { sx, sy, cropWidth, cropHeight } = cropRect
        ? computeExplicitCropFromSensor(originalWidth, originalHeight, cropRect)
        : computeBitmapCropFromSensor(
            originalWidth,
            originalHeight,
            effectiveCameraRatio,
            fillContainer,
            ratio
          )

      return {
        sx,
        sy,
        cropWidth,
        cropHeight,
        originalWidth,
        originalHeight
      }
    } catch (error) {
      console.error('Error in getBitmapParam:', error)
      return undefined
    }
  }, [
    capturePreview,
    effectiveCameraRatio,
    previewQuality,
    cameraKey,
    fillContainer,
    ratio,
    cropRect,
    captureShareRole,
    captureShareGroupId
  ])

  /** Report canvas backing-store size for WebCodecs recorders (must match encoder width/height). */
  const reportPreviewCanvasDimensions = useCallback((): void => {
    const cb = onCanvasReadyRef.current
    if (!cb) return
    const el = canvasRef.current
    if (!el || el.width <= 0 || el.height <= 0) return
    const w = el.width
    const h = el.height
    const last = lastReportedCanvasDimsRef.current
    if (last && last.width === w && last.height === h) return
    lastReportedCanvasDimsRef.current = { width: w, height: h }
    cb({ width: w, height: h })
  }, [])

  useEffect(() => {
    // Initialize stats if debug mode is on
    if (debugFps && statsRef.current && previewRef.current) {
      previewRef.current.appendChild(statsRef.current.dom)
    }

    return (): void => {
      if (statsRef.current?.dom && previewRef.current?.contains(statsRef.current.dom)) {
        previewRef.current.removeChild(statsRef.current.dom)
      }
    }
  }, [debugFps])

  useEffect(() => {
    // When paused, stop preview refresh loop
    if (isPaused) {
      isUnmountedRef.current = true
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = undefined
      }
      return
    }

    isUnmountedRef.current = false
    // This run owns the loop from here on (see previewRunIdRef).
    const runId = previewRunIdRef.current + 1
    previewRunIdRef.current = runId
    /** Stop conditions for this run's loop: unmounted, paused, or superseded by a newer run. */
    const shouldStop = (): boolean =>
      isUnmountedRef.current || isPaused || previewRunIdRef.current !== runId
    if (!capturePreview) return

    const container = containerRef.current
    const preview = previewRef.current
    const canvas = canvasRef.current
    const overlay = overlayRef.current
    if (!container || !preview || !canvas || !overlay) return

    // Get canvas context for bitmap rendering
    const canvasCtx = canvas.getContext('bitmaprenderer')
    if (!canvasCtx) return

    /**
     * Recording buffer matches the filtered preview bitmap (same pixels as display), with
     * rotation/mirror baked for WebCodecs — avoids readback via createImageBitmap(display).
     */
    const syncRecordingFromBitmap = (bmp: ImageBitmap): void => {
      const rec = recordingCanvasRef.current
      const cbRec = onRecordingCanvasReadyRef.current
      if (!rec || !cbRec || bmp.width <= 0 || bmp.height <= 0) return

      const norm = ((rotation % 360) + 360) % 360
      const isRot90or270 = norm === 90 || norm === 270
      const rawRecW = isRot90or270 ? bmp.height : bmp.width
      const rawRecH = isRot90or270 ? bmp.width : bmp.height
      const roundedRecW = Math.max(2, Math.round(rawRecW / 2) * 2)
      const roundedRecH = Math.max(2, Math.round(rawRecH / 2) * 2)

      if (rec.width !== roundedRecW || rec.height !== roundedRecH) {
        rec.width = roundedRecW
        rec.height = roundedRecH
        const rctxFresh = rec.getContext('2d')
        if (rctxFresh) {
          const lastR = lastReportedRecordingDimsRef.current
          if (!lastR || lastR.width !== roundedRecW || lastR.height !== roundedRecH) {
            lastReportedRecordingDimsRef.current = { width: roundedRecW, height: roundedRecH }
            cbRec(rec, rctxFresh, { width: roundedRecW, height: roundedRecH })
          }
        }
      }

      const recCtx = rec.getContext('2d')
      if (!recCtx) return

      const w = bmp.width
      const h = bmp.height
      const rw = rec.width
      const rh = rec.height
      recCtx.setTransform(1, 0, 0, 1, 0, 0)
      recCtx.clearRect(0, 0, rw, rh)
      recCtx.translate(rw / 2, rh / 2)
      if (norm === 0 || norm === 180) {
        if (mirror) recCtx.scale(-1, 1)
        if (norm !== 0) recCtx.rotate((norm * Math.PI) / 180)
      } else {
        recCtx.rotate((norm * Math.PI) / 180)
        if (mirror) recCtx.scale(1, -1)
      }
      recCtx.drawImage(bmp, -w / 2, -h / 2)
    }
    syncRecordingFromBitmapRef.current = syncRecordingFromBitmap

    // Reset loading and error state when camera changes
    // Check if camera actually changed
    const cameraChanged = currentCameraKeyRef.current !== cameraKey
    if (cameraChanged) {
      currentCameraKeyRef.current = cameraKey
      lastReportedCanvasDimsRef.current = null
      lastReportedRecordingDimsRef.current = null
      sensorAspectRef.current = undefined // reset so stale sensor dims don't pollute new camera layout
      lastLutWorkerFilterRef.current = ''
      setIsCanvasLoaded(false)
      setError(undefined)
    }

    let bitmapParams: Awaited<ReturnType<typeof getBitmapParam>>

    /**
     * The crop rectangle is measured ONCE from a camera frame and then reused for every
     * frame after it, so it has to be re-measured whenever this slot's device changes.
     * Picking another camera in the settings modal restarts this effect the instant the
     * selection changes — before `tryToConnect` has actually swapped the device — so the
     * measurement lands on the OUTGOING camera and every later frame is cropped with the
     * wrong rectangle. A stale rect larger than the new camera's frame makes
     * `createImageBitmap` pad the remainder with transparent pixels, and the feed appears
     * shrunk into one corner of the preview box; a smaller one zooms the feed in. The
     * secondary slot never restarted the effect at all, so there it never healed.
     *
     * The handles are the honest signal — a connect replaces the object — and deviceType
     * rides along because the DSLR and webcam handles are nulled independently.
     */
    const readDeviceStamp = (): PreviewDeviceStamp =>
      readPreviewDeviceStamp(useCameraStore.getState(), cameraKey)
    /** The device `bitmapParams` was measured from; null until the first measurement. */
    let measuredFrom: PreviewDeviceStamp | null = null

    /**
     * Measure the crop rect, throwing away a measurement the device changed underneath —
     * that is exactly the frame a mid-switch measurement lands on.
     */
    const measureBitmapParams = async (): Promise<typeof bitmapParams> => {
      const before = readDeviceStamp()
      const params = await getBitmapParam()
      if (!params) return undefined
      const after = readDeviceStamp()
      if (!isSamePreviewDevice(before, after)) return undefined
      measuredFrom = after
      return params
    }

    const updateCanvasSize = (): void => {
      if (throttledRef.current) {
        cancelAnimationFrame(throttledRef.current)
      }

      throttledRef.current = requestAnimationFrame(() => {
        throttledRef.current = 0

        const containerWidth = container.offsetWidth
        const containerHeight = container.offsetHeight

        // ── Rotation-aware layout ────────────────────────────────────────────────
        // A CSS rotate(90°/270°) on the canvas element changes its *visual* footprint
        // (width↔height swap) but does NOT change its layout box.  Stretching the
        // canvas to '100%' of its (un-rotated) parent would squeeze/stretch the image.
        //
        // Correct approach:
        //  1. Compute the previewDiv size using the *post-rotation* aspect ratio so it
        //     occupies the right amount of screen space.
        //  2. Keep the canvas at its *pre-rotation* natural pixel size (previewHeight × previewWidth
        //     for 90°/270°) with explicit px values — no '100%' stretching.
        //  3. Center the canvas inside previewDiv absolutely, then apply the CSS rotate.
        //     After rotation the canvas visual footprint = previewDiv size exactly.
        // ────────────────────────────────────────────────────────────────────────

        const isRotated90or270 = rotation === 90 || rotation === 270

        // The aspect ratio *as it will appear on screen* after CSS rotation.
        // For 90/270°, width and height swap, so the ratio inverts.
        const displayCameraRatio =
          effectiveCameraRatio !== undefined && isRotated90or270
            ? 1 / effectiveCameraRatio
            : effectiveCameraRatio

        // Strip placeholders: L3 bitmap aspect matches slot `ratio`; cover-layout must use that
        // (not only camera crop aspect) or the inner box is sized wrong and the feed looks shrunk.
        const slotFillRatio =
          fillContainer &&
          ratio !== undefined &&
          typeof ratio === 'number' &&
          Number.isFinite(ratio) &&
          ratio > 0
            ? isRotated90or270
              ? 1 / ratio
              : ratio
            : undefined
        const coverSourceRatio = slotFillRatio ?? displayCameraRatio

        // Step 1: size the previewDiv to fit the rotated visual footprint within the container.
        let previewWidth: number, previewHeight: number
        if (fillContainer) {
          // Cover behavior needs a source aspect. With no explicit ratio and captureRatio
          // 'auto', fall back to the live sensor aspect (same fallback the non-fill branch
          // uses; getBitmapParam re-runs layout when it's first known). Without this the
          // canvas was stretched to the container's own aspect — a visibly distorted feed
          // whenever the pane shape didn't match the camera (e.g. portrait kotak panes on
          // a 16:9 webcam). Stretching contradicts the prop's documented object-cover
          // contract; only the pre-first-frame moment keeps the plain container fill.
          const sensorDisplayRatio =
            sensorAspectRef.current !== undefined
              ? isRotated90or270
                ? 1 / sensorAspectRef.current
                : sensorAspectRef.current
              : undefined
          const coverRatio = coverSourceRatio ?? sensorDisplayRatio
          if (coverRatio === undefined) {
            previewWidth = containerWidth
            previewHeight = containerHeight
          } else {
            const scaleBasedOnWidth = containerWidth / coverRatio
            const scaleBasedOnHeight = containerHeight
            const scale = Math.max(scaleBasedOnWidth, scaleBasedOnHeight)
            previewWidth = scale * coverRatio
            previewHeight = scale
          }
        } else if (displayCameraRatio === undefined) {
          // 'auto' — no explicit crop ratio set. Use the real sensor aspect if available;
          // fall back to the container ratio (i.e. fill without stretching) until the first
          // camera frame arrives.  Never hardcode 4:3 — that forces a crop box on any sensor
          // that isn't exactly 4:3 (e.g. 16:9 webcams, 3:2 DSLRs).
          const fallbackRatio = sensorAspectRef.current ?? (containerWidth / containerHeight)
          const isRotatedForLayout = isRotated90or270 // already computed above
          const displayFallback = isRotatedForLayout ? 1 / fallbackRatio : fallbackRatio
          if (displayFallback >= 1) {
            previewWidth = Math.min(containerWidth, containerHeight * displayFallback)
            previewHeight = previewWidth / displayFallback
          } else {
            previewHeight = Math.min(containerHeight, containerWidth / displayFallback)
            previewWidth = previewHeight * displayFallback
          }
        } else if (displayCameraRatio >= 1) {
          previewWidth = Math.min(containerWidth, containerHeight * displayCameraRatio)
          previewHeight = previewWidth / displayCameraRatio
        } else {
          previewHeight = Math.min(containerHeight, containerWidth / displayCameraRatio)
          previewWidth = previewHeight * displayCameraRatio
        }

        // Set previewDiv size and center it in the container.
        preview.style.width = `${previewWidth}px`
        preview.style.height = `${previewHeight}px`
        preview.style.left = `${(containerWidth - previewWidth) / 2}px`
        preview.style.top = `${(containerHeight - previewHeight) / 2}px`

        // Step 2: set the canvas pixel buffer and CSS display size.
        // For 0°/180°: canvas display size = previewDiv size (no rotation swap needed).
        // For 90°/270°: the canvas will be physically rotated 90°, so its pre-rotation
        //   natural display size must be (previewHeight × previewWidth) — after the CSS
        //   rotate these become (previewWidth × previewHeight) on screen, matching previewDiv.
        let canvasDisplayW: number, canvasDisplayH: number
        if (isRotated90or270) {
          canvasDisplayW = previewHeight  // canvas "width" before rotation = target height after
          canvasDisplayH = previewWidth   // canvas "height" before rotation = target width after
        } else {
          canvasDisplayW = previewWidth
          canvasDisplayH = previewHeight
        }

        // Pixel buffer matches the display size (1:1 logical pixels, no extra scaling).
        canvas.width = Math.round(canvasDisplayW)
        canvas.height = Math.round(canvasDisplayH)

        // Recording canvas dimensions are driven in syncRecordingFromDisplay from the real
        // camera bitmap size (after transferFromImageBitmap), not layout — avoids stale rec size.

        // Pin the canvas to an explicit pixel size — never use '100%' after rotation because
        // that stretches the canvas to fill its (un-rotated) parent box.
        canvas.style.width = `${canvasDisplayW}px`
        canvas.style.height = `${canvasDisplayH}px`

        // Step 3: center the canvas absolutely inside previewDiv, then rotate.
        // After rotate(90deg), the visual footprint becomes canvasDisplayH × canvasDisplayW
        // = previewWidth × previewHeight, which exactly fills previewDiv.
        canvas.style.position = 'absolute'
        canvas.style.top = '50%'
        canvas.style.left = '50%'

        // Build the full transform: translate-to-center → rotate → mirror.
        // getRotationAwareMirrorTransform returns scaleX(-1) or scaleY(-1) to produce a
        // correct visual horizontal flip given the current CSS rotation angle.
        const mirrorTransform = imageUtils.getRotationAwareMirrorTransform(rotation || 0, mirror)
        const translateTransform = 'translate(-50%, -50%)'
        const rotateTransform = rotation ? `rotate(${rotation}deg)` : ''
        canvas.style.transform = [translateTransform, rotateTransform, mirrorTransform]
          .filter(Boolean)
          .join(' ')
        canvas.style.transformOrigin = 'center center'

        // Calculate frame capture area dimensions (overlay mask).
        // Use the screen-space (post-rotation) previewDiv dimensions as the reference.
        const overlayWidth = fillContainer ? containerWidth : previewWidth
        const overlayHeight = fillContainer ? containerHeight : previewHeight

        // L3 highlight: only when parent passes explicit `ratio` (frame slot / display box).
        // Never use store captureRatio here — that caused phantom masks in SettingsModal (no frame).
        const visibleCameraRatio =
          displayCameraRatio !== undefined
            ? displayCameraRatio
            : sensorAspectRef.current !== undefined
              ? isRotated90or270
                ? 1 / sensorAspectRef.current
                : sensorAspectRef.current
              : undefined

        const overlayRatio =
          ratio === undefined ? undefined : isRotated90or270 ? 1 / ratio : ratio

        let captureWidth = 0
        let captureHeight = 0
        if (overlayRatio !== undefined) {
          if (overlayRatio >= 1) {
            captureWidth = Math.min(overlayWidth, overlayHeight * overlayRatio)
            captureHeight = captureWidth / overlayRatio
          } else {
            captureHeight = Math.min(overlayHeight, overlayWidth / overlayRatio)
            captureWidth = captureHeight * overlayRatio
          }
        }

        // Only show frame overlay when explicit ratio differs from visible camera (screen-space).
        if (
          !fillContainer &&
          overlayRatio !== undefined &&
          visibleCameraRatio !== undefined &&
          Math.abs(overlayRatio - visibleCameraRatio) > 0.01
        ) {
          const captureLeft = (overlayWidth - captureWidth) / 2
          const captureTop = (overlayHeight - captureHeight) / 2

          overlay.innerHTML = `
            <svg width="${overlayWidth}" height="${overlayHeight}" style="position: absolute; inset: 0;">
              <defs>
                <mask id="mask">
                  <rect width="100%" height="100%" fill="white"/>
                  <rect x="${captureLeft}" y="${captureTop}" width="${captureWidth}" height="${captureHeight}" fill="black"/>
                </mask>
              </defs>
              <rect width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#mask)"/>
            </svg>
          `
        } else {
          overlay.innerHTML = ''
        }

        reportPreviewCanvasDimensions()
      })
    }

    updateCanvasSizeRef.current = updateCanvasSize

    const resizeObserver = new ResizeObserver(() => {
      updateCanvasSize()
    })
    resizeObserver.observe(container)
    updateCanvasSize()

    const refreshPreview = async (retryCount = 0): Promise<void> => {
      if (shouldStop()) return

      try {
        const cameraStoreState = useCameraStore.getState()
        const deviceTypeForKey =
          cameraKey === 'primary' ? cameraStoreState.deviceType : cameraStoreState.secondaryDeviceType
        const previewMaxFps =
          settings.previewQuality?.maxFps ??
          (deviceTypeForKey === 'camera' ? DEFAULT_DSLR_PREVIEW_MAX_FPS : undefined)
        if (
          previewMaxFps != null &&
          previewMaxFps > 0 &&
          !onRecordingCanvasReadyRef.current
        ) {
          const minMs = 1000 / previewMaxFps
          const now = performance.now()
          if (now - lastPreviewFrameTimeRef.current < minMs) {
            if (!shouldStop()) {
              animationRef.current = requestAnimationFrame(refreshPreview)
            }
            return
          }
          lastPreviewFrameTimeRef.current = now
        }

        // The cached crop rect belongs to the device it was measured from; a switch in the
        // settings modal invalidates it (see readDeviceStamp).
        if (bitmapParams && !isSamePreviewDevice(measuredFrom, readDeviceStamp())) {
          bitmapParams = undefined
        }
        if (!bitmapParams) {
          bitmapParams = await measureBitmapParams()
          if (!bitmapParams) {
            // Retry after a short delay if camera might not be ready yet
            if (retryCount < 5) {
              await new Promise((resolve) => setTimeout(resolve, 100 * (retryCount + 1)))
              return refreshPreview(retryCount + 1)
            }
            // A camera that is still connecting (a DSLR takes seconds) outlives the retry
            // ladder. Keep the loop alive so the feed comes back on its own — returning
            // here left a dead canvas until something else restarted the effect, and now
            // that a switch re-measures mid-loop that would strand every slow connect.
            setError('Camera not ready. Please try switching cameras or refreshing.')
            if (!shouldStop()) {
              animationRef.current = requestAnimationFrame(() => refreshPreview())
            }
            return
          }
        }

        let blob: Blob | undefined
        if (captureShareRole === 'consumer' && captureShareGroupId) {
          const shared = peekSharedPreviewCapture(captureShareGroupId)
          if (!shared?.blob || shared.cameraKey !== cameraKey) {
            if (retryCount < 5) {
              await new Promise((resolve) => setTimeout(resolve, 100 * (retryCount + 1)))
              return refreshPreview(retryCount + 1)
            }
            setError('Failed to capture from camera')
            return
          }
          blob = shared.blob
        } else {
          blob = await capturePreview(cameraKey, {
            ...previewQuality,
            rotation: 0,
            mirror: false
          })
        }

        if (!blob) {
          if (retryCount < 5) {
            await new Promise((resolve) => setTimeout(resolve, 100 * (retryCount + 1)))
            return refreshPreview(retryCount + 1)
          }
          setError('Failed to capture from camera')
          return
        }
        if (shouldStop()) return

        if (captureShareRole === 'producer' && captureShareGroupId) {
          publishSharedPreviewCapture(
            captureShareGroupId,
            cameraKey,
            blob,
            bitmapParams.originalWidth,
            bitmapParams.originalHeight
          )
        }

        const { sx, sy, cropWidth, cropHeight } = bitmapParams

        const cropAspect = cropWidth / cropHeight
        let cssW = canvas.width
        let cssH = canvas.height
        const displayAspect = (): number =>
          cssW > 0 && cssH > 0 ? cssW / cssH : cropAspect
        if (Math.abs(cropAspect - displayAspect()) > 0.01) {
          updateCanvasSizeRef.current?.()
          await new Promise<void>((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                resolve()
              })
            })
          })
          cssW = canvas.width
          cssH = canvas.height
        }

        // Fit the crop region inside previewQuality's max box while preserving its aspect ratio.
        // Forcing resize to maxWidth×maxHeight would stretch non-matching-aspect crops (e.g. a
        // 4:3 crop into the default 1280×720 box), leaving the canvas backing store with the
        // wrong aspect and poisoning every downstream recorder.
        const maxW = settings.previewQuality?.maxWidth ?? 1280
        const maxH = settings.previewQuality?.maxHeight ?? 720
        const fitScale = Math.min(maxW / cropWidth, maxH / cropHeight, 1)
        const targetW = Math.max(2, Math.round((cropWidth * fitScale) / 2) * 2)
        const targetH = Math.max(2, Math.round((cropHeight * fitScale) / 2) * 2)

        const croppedImg = await createImageBitmap(blob, sx, sy, cropWidth, cropHeight, {
          resizeQuality: settings.previewQuality?.resizeQuality || 'medium',
          resizeWidth: targetW,
          resizeHeight: targetH
        })

        if (applyFilterOnPreview && (adjustments || (filter && getCachedLUT(filter)))) {
          const lutData = filter ? getCachedLUT(filter) : null
          // Keep the worker's overlay in step before either frame path uses it. Cheap:
          // the signature only changes when the operator picks a different filter or
          // edits the overlay, not once per frame.
          syncOverlayToWorker()
          if (lutData) {
            const worker = filterWorkerRef.current
            if (filter && lastLutWorkerFilterRef.current !== filter) {
              const data = new Float32Array(lutData.data)
              worker?.postMessage(
                {
                  type: 'lutInit',
                  lutData: { size: lutData.size, data, title: lutData.title }
                },
                { transfer: [data.buffer] }
              )
              lastLutWorkerFilterRef.current = filter
            }
            if (!isWorkerBusyRef.current && worker) {
              isWorkerBusyRef.current = true
              worker.postMessage(
                {
                  type: 'lutFrame',
                  bitmap: croppedImg,
                  postLUTAdjustments
                },
                [croppedImg]
              )
            } else {
              croppedImg.close()
            }
          } else if (adjustments) {
            if (!isWorkerReadyRef.current) {
              const offscreenCanvas = new OffscreenCanvas(croppedImg.width, croppedImg.height)
              const offscreenCtx = offscreenCanvas.getContext('2d', { willReadFrequently: true })

              if (offscreenCtx) {
                offscreenCtx.drawImage(croppedImg, 0, 0)
                filterUtils.applyAdjustments(offscreenCtx, adjustments)
                // Same overlay on the main-thread path, or the first frames after a
                // filter change would show the grade without its overlay and then snap.
                applyFilterOverlayImage(offscreenCtx, filterOverlay)
                const filteredBitmap = offscreenCanvas.transferToImageBitmap()
                syncRecordingFromBitmapRef.current?.(filteredBitmap)
                canvasCtx?.transferFromImageBitmap(filteredBitmap)
              }
              if (!isWorkerBusyRef.current) {
                isWorkerBusyRef.current = true
                filterWorkerRef.current?.postMessage({ bitmap: croppedImg, adjustments }, [
                  croppedImg
                ])
              } else {
                croppedImg.close()
              }
            } else {
              if (!isWorkerBusyRef.current) {
                isWorkerBusyRef.current = true
                filterWorkerRef.current?.postMessage({ bitmap: croppedImg, adjustments }, [
                  croppedImg
                ])
              } else {
                croppedImg.close()
              }
            }
          }
        } else {
          syncRecordingFromBitmapRef.current?.(croppedImg)
          canvasCtx?.transferFromImageBitmap(croppedImg)
        }

        reportPreviewCanvasDimensions()
        setIsCanvasLoaded(true)
      } catch (err) {
        console.error('Preview refresh failed:', err)
        lastFrameErrorRef.current = describeError(err)
        setError('Failed to load camera preview')
        setIsCanvasLoaded(false)
      }

      if (!shouldStop()) {
        animationRef.current = requestAnimationFrame(refreshPreview)
        statsRef.current?.update()
      }
    }

    // Initialize and start preview with a small delay to ensure camera is ready
    const initializePreview = async (): Promise<void> => {
      // Small delay to allow camera connection to stabilize after switching
      await new Promise((resolve) => setTimeout(resolve, 150))
      if (shouldStop()) return

      // Handle live view brightness feature
      const shouldApplyBrightness =
        settings?.autoBrightenLiveViewForFlash && settings?.useShutterCapture

      if (!shouldApplyBrightness && isLiveViewBrightnessAppliedRef.current) {
        // Restore original settings if toggle was disabled
        try {
          if (originalSettingsRef.current) {
            await restoreOriginalSettings(cameraKey, originalSettingsRef.current)
            originalSettingsRef.current = null
            isLiveViewBrightnessAppliedRef.current = false
            console.log('[Preview] Restored original settings (toggle disabled)')
          }
        } catch (error) {
          console.error('[Preview] Error restoring original settings:', error)
        }
      }

      bitmapParams = await measureBitmapParams()
      if (bitmapParams) {
        // Only attempt live-view brightness adjustment after preview is confirmed responsive.
        // This avoids hammering config APIs while the camera is still initializing/busy.
        if (shouldApplyBrightness && !isLiveViewBrightnessAppliedRef.current) {
          try {
            // Save original settings first
            const original = await saveOriginalSettings(cameraKey)
            if (original) {
              originalSettingsRef.current = original

              // Apply live view brightness settings
              const applied = await applyLiveViewBrightness(cameraKey, {
                autoBrightenLiveViewForFlash: settings.autoBrightenLiveViewForFlash,
                liveViewShutterSpeedOverride: settings.liveViewShutterSpeedOverride
              })

              if (applied) {
                isLiveViewBrightnessAppliedRef.current = true
                console.log('[Preview] Applied live view brightness settings')
              } else {
                console.warn('[Preview] Failed to apply live view brightness settings')
                // Note: We don't show toast here to avoid spamming during preview initialization
                // The UI already shows a warning if the camera doesn't support the feature
              }
            }
          } catch (error) {
            console.error('[Preview] Error applying live view brightness:', error)
          }
        }

        refreshPreview()
      } else {
        // Retry initialization if camera not ready
        setTimeout(() => {
          if (!shouldStop()) {
            initializePreview()
          }
        }, 200)
      }
    }

    initializePreview()

    return (): void => {
      isUnmountedRef.current = true
      updateCanvasSizeRef.current = null
      syncRecordingFromBitmapRef.current = null
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      resizeObserver.disconnect()

      // Restore original settings on unmount if they were applied
      if (isLiveViewBrightnessAppliedRef.current && originalSettingsRef.current) {
        restoreOriginalSettings(cameraKey, originalSettingsRef.current).catch((error) => {
          console.error('[Preview] Error restoring settings on unmount:', error)
        })
        originalSettingsRef.current = null
        isLiveViewBrightnessAppliedRef.current = false
      }
    }
  }, [
    capturePreview,
    effectiveCameraRatio,
    ratio,
    getBitmapParam,
    mirror,
    reportPreviewCanvasDimensions,
    adjustments,
    filter,
    postLUTAdjustments,
    filterOverlay,
    syncOverlayToWorker,
    applyFilterOnPreview,
    cameraKey,
    previewQuality,
    settings?.previewQuality,
    settings?.autoBrightenLiveViewForFlash,
    settings?.liveViewShutterSpeedOverride,
    settings?.useShutterCapture,
    rotation,
    isPaused,
    selectedCamera,
    fillContainer,
    settings,
    captureShareRole,
    captureShareGroupId
  ])

  return (
    <div ref={containerRef} className={`w-full h-full relative overflow-hidden ${fillContainer ? '' : 'bg-black'}`}>
      {!capturePreview ? (
        <div className="absolute inset-0 flex items-center justify-center text-white">
          Please select a camera
        </div>
      ) : (
        <div ref={previewRef} className="absolute" style={{ overflow: 'hidden' }}>
          <canvas ref={canvasRef} className="absolute" data-preview-type="camera" />
          <canvas
            ref={recordingCanvasRef}
            aria-hidden
            data-preview-type="camera-recording"
            className="absolute pointer-events-none"
            style={{ width: 0, height: 0, opacity: 0, position: 'fixed', left: -9999, top: 0 }}
          />
          <div ref={overlayRef} className="absolute inset-0" />
        </div>
      )}
      {!isCanvasLoaded && (
        <div className="absolute inset-0 z-[1] flex flex-col gap-2 justify-center items-center text-white">
          {error ? (
            <span>{error}</span>
          ) : (
            <>
              <Spinner />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default Preview
